import {
  ComponentProps,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { OkButton } from 'src/components/Buttons/OkButton';
import { UpdateCampaignStateButton } from 'src/components/SponsoredListings/UpdateCampaignStateButton';
import {
  AdCampaignsDataContextProvider,
  useAdCampaignsDataContext,
} from 'src/contexts/AdCampaignsDataContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useContent } from 'src/contexts/ContentContext';
import { EventMetricsProvider } from 'src/contexts/EventMetricsContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Stack } from 'src/core/ui';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getFormattedEventName,
  lookupEventInCatalog,
} from 'src/utils/eventWithDataUtils';
import {
  hasChanged,
  posChangedField,
  posField,
  posFieldFromValues,
} from 'src/utils/posFieldUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  AdCampaignState,
  BiddingMode,
  CampaignManagerClient,
  Event,
  FlightDuration,
  ListingMetrics,
  SponsoredListingsCampaign,
  SponsoredListingsCampaignInput,
} from 'src/WebApiController';

import { CancellableFormFooter } from '../../common';
import { CancellableFormHeader } from '../../common/CancellableFormHeader';
import { modalDetails } from '../../common/Modals.css';
import { ModalProps } from '../../Modal';
import { ModalBody, ModalFooter } from '../../Modal/Modal.styled';
import { getProspectiveBudget } from '../sponsoredListings.util';
import * as styles from './CampaignModal.css';
import { CampaignModalBody } from './CampaignModalBody';
import { CampaignModalFooter } from './CampaignModalFooter';
import { CampaignModalHeader } from './CampaignModalHeader';
import { CampaignModalIntroBody } from './CampaignModalIntroBody';
import { CampaignModalIntroHeader } from './CampaignModalIntroHeader';

type CampaignModalProps = {
  campaign?: SponsoredListingsCampaign | null;
  eventId: number;
  cancelTo?: ModalProps;
};

export const CampaignModal = ({
  campaign,
  eventId,
  cancelTo,
}: CampaignModalProps) => {
  const { data } = useCatalogDataContext();
  const event = lookupEventInCatalog(data, eventId.toString());

  const { eventMetrics } = useCatalogMetricsContext<ListingMetrics>();
  const metrics = useMemo(
    () =>
      event?.event?.virtId ? eventMetrics?.[event.event.virtId] : undefined,
    [eventMetrics, event?.event?.virtId]
  );

  const { performer, venue } = useGetEventFullInfo(event?.event);
  const [mainText, connectText, subText] = getFormattedEventName({
    event: event?.event,
    performer,
    venue,
  });

  const eventNameDisplay = `${mainText} ${connectText?.trim()} ${subText}`;

  const methods = useForm<SponsoredListingsCampaignInput>({
    defaultValues: {
      eventId,
      campaignId: campaign?.campaignId ?? -1, // negative value indicates new campaign
      biddingMode: posFieldFromValues(
        campaign?.biddingMode ?? BiddingMode.Automatic,
        campaign?.biddingMode
      ),
      targetTicketClassIds: posFieldFromValues(
        campaign?.targetTicketClassIds,
        campaign?.targetTicketClassIds
      ),
      campaignName: posFieldFromValues(
        campaign?.campaignName ||
          `${eventNameDisplay} - ${FlightDuration.Daily}`,
        campaign?.campaignName
      ),
      flightDuration: posFieldFromValues(
        campaign?.flightDuration || FlightDuration.Daily,
        campaign?.flightDuration
      ),
      recurringFlightBudget: posFieldFromValues(
        campaign?.activeFlight?.flightBudget ||
          campaign?.recurringFlightBudget ||
          getProspectiveBudget(
            metrics?.unsoldCst,
            FlightDuration.Daily,
            event?.event?.dates?.start
          ),
        campaign?.recurringFlightBudget
      ),
      dateRange: campaign?.dateRange || {
        start: new Date().toISOString(),
        end: event?.event?.dates?.start ?? null,
      },
      editBudgetForActiveFlightOnly: campaign?.activeFlight
        ? posChangedField(true)
        : posField(false),
    },
  });

  if (!event) {
    return null;
  }

  return (
    <AdCampaignsDataContextProvider loadSalesDetails>
      <FormProvider {...methods}>
        <CampaignModalContent
          cancelTo={cancelTo}
          eventId={eventId}
          event={event.event}
          campaign={campaign}
          eventNameDisplay={eventNameDisplay}
          metrics={metrics}
          {...methods}
        />
      </FormProvider>
    </AdCampaignsDataContextProvider>
  );
};

export const CampaignModalContent = ({
  event,
  campaign,
  metrics,
  eventNameDisplay,
  cancelTo,
  formState,
  handleSubmit,
  watch,
  getValues,
  clearErrors,
  setError,
}: Omit<
  ComponentProps<typeof FormProvider<SponsoredListingsCampaignInput, unknown>>,
  'children'
> &
  CampaignModalProps & {
    event: Event;
    eventNameDisplay: string;
    metrics?: ListingMetrics;
  }) => {
  const { activeAccountWebClientConfig } = useAppContext();

  const { closeModal } = useContext(ModalContext);
  const { showErrorDialog } = useClientLogContext();
  const [isLoading, setIsLoading] = useState(false);

  const { refreshData } = useAdCampaignsDataContext();

  const requiredText = useContent(ContentId.Required);

  const validateForm = useCallback(
    (formValues: SponsoredListingsCampaignInput): boolean => {
      let isValid = true;

      // Clear all previous errors
      clearErrors();

      // Validate campaign name
      if (
        !formValues.campaignName.value ||
        formValues.campaignName.value.trim().length === 0
      ) {
        setError('campaignName', {
          type: 'required',
          message: requiredText,
        });
        isValid = false;
      }

      // Validate budget
      if (
        !formValues.recurringFlightBudget.value ||
        formValues.recurringFlightBudget.value <= 0
      ) {
        setError('recurringFlightBudget', {
          type: 'required',
          message: requiredText,
        });
        isValid = false;
      }

      return isValid;
    },
    [clearErrors, setError, requiredText]
  );

  const onSubmit = useCallback(
    async (formValue: SponsoredListingsCampaignInput) => {
      setIsLoading(true);
      tryInvokeApi(
        async () => {
          const client = new CampaignManagerClient(
            activeAccountWebClientConfig
          );
          const result = await client.mergeCampaign(formValue);
          if (!result.success) {
            showErrorDialog('Sponsored Listing', {
              message: result.errorMessage,
            });
            return;
          }
          await refreshData();
          closeModal(true);
        },
        (error) => {
          showErrorDialog('Sponsored Listing', error, {
            trackErrorData: formValue,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [activeAccountWebClientConfig, closeModal, refreshData, showErrorDialog]
  );

  const hasChanges = useCallback(
    (formValue: SponsoredListingsCampaignInput) => {
      if (formState.defaultValues) {
        if (formValue.campaignId < 0) {
          // New campaign - all fields are changed
          return true;
        }

        return (
          hasChanged(
            formValue.campaignName,
            formState.defaultValues.campaignName
          ) ||
          hasChanged(
            formValue.flightDuration,
            formState.defaultValues.flightDuration
          ) ||
          hasChanged(
            formValue.recurringFlightBudget,
            formState.defaultValues.recurringFlightBudget
          ) ||
          hasChanged(
            formValue.targetTicketClassIds,
            formState.defaultValues.targetTicketClassIds
          )
        );
      }
      return false;
    },
    [formState.defaultValues]
  );

  const formHasChanges = hasChanges(watch());

  const { hasCampaignsQuery } = useAdCampaignsDataContext();
  const [viewedIntroPage, setViewedIntroPage] = useState(false);

  const showIntroPage = useMemo(
    () => !campaign && !hasCampaignsQuery.data && !viewedIntroPage,
    [campaign, hasCampaignsQuery.data, viewedIntroPage]
  );

  const onSubmitHandler = useCallback(() => {
    if (showIntroPage) {
      setViewedIntroPage(true);
      return;
    }

    const formValues = getValues();

    // Validate the form
    const isFormValid = validateForm(formValues);

    if (isFormValid) {
      handleSubmit(onSubmit)();
    }
  }, [showIntroPage, getValues, validateForm, handleSubmit, onSubmit]);

  return (
    <EventMetricsProvider event={event}>
      <CancellableFormHeader
        className={styles.modalHeader}
        cancelTo={cancelTo}
        disabled={isLoading || formState.isSubmitting}
        showDialogOnCancel={formHasChanges}
      >
        {!showIntroPage ? (
          <CampaignModalHeader
            event={event}
            eventNameDisplay={eventNameDisplay}
            metrics={metrics}
          />
        ) : (
          <CampaignModalIntroHeader />
        )}
      </CancellableFormHeader>

      {hasCampaignsQuery.isLoading ? (
        <PosSpinner />
      ) : (
        <ModalBody>
          <div className={modalDetails}>
            {!showIntroPage ? (
              <CampaignModalBody
                campaign={campaign}
                disabled={isLoading}
                eventNameDisplay={eventNameDisplay}
                event={event}
                metrics={metrics}
              />
            ) : (
              <CampaignModalIntroBody />
            )}
          </div>
        </ModalBody>
      )}

      <ModalFooter>
        <Stack direction="column" width="full" gap="l">
          {!showIntroPage && <CampaignModalFooter />}
          <CancellableFormFooter
            cancelTo={cancelTo}
            disabled={isLoading}
            showDialogOnCancel={formHasChanges}
            hideCloseButton
          >
            <Stack justifyContent="spaceBetween">
              {campaign && (
                <UpdateCampaignStateButton
                  className={styles.boostButtonCommon}
                  campaign={campaign}
                  state={
                    campaign?.campaignState === AdCampaignState.Active
                      ? AdCampaignState.Paused
                      : AdCampaignState.Active
                  }
                  disabled={isLoading}
                  onBeforePause={() => setIsLoading(true)}
                  onAfterPause={async (success) => {
                    if (success) {
                      await refreshData?.();
                      setIsLoading(false);
                      closeModal(true);
                    }
                  }}
                />
              )}
              <OkButton
                className={styles.boostButtonCommon}
                style={{ width: campaign ? 'auto' : '100%' }}
                variant="regular"
                shape="rect"
                onClick={onSubmitHandler}
                disabled={
                  !formHasChanges || (!showIntroPage ? isLoading : false)
                }
                textContentId={
                  campaign
                    ? ContentId.Update
                    : hasCampaignsQuery.data || viewedIntroPage
                      ? ContentId.BoostEvent
                      : ContentId.GetStarted
                }
              />
            </Stack>
          </CancellableFormFooter>
        </Stack>
      </ModalFooter>
    </EventMetricsProvider>
  );
};
